"use client";

import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { track } from "@/lib/observability/analytics";
import {
  MagnifyingGlass,
  X,
  Barcode,
  Pill,
  Flask,
  Tag,
  Buildings,
  Question,
  Sparkle,
  Spinner,
} from "@phosphor-icons/react";

type Suggestion = {
  kind: "medicament" | "dci" | "laboratoire" | "classe";
  label: string;
  hint?: string;
  slug?: string;
  href: string;
};

type Props = {
  defaultValue?: string;
};

type DetectedKind = "barcode" | "atc" | "symptom" | "free";

function detectKind(q: string): DetectedKind {
  const t = q.trim();
  if (!t) return "free";
  if (/^\d{8,14}$/.test(t)) return "barcode";
  if (/^[A-Z]\d{2}([A-Z]{1,2}\d{0,2})?$/i.test(t)) return "atc";
  if (/\b(douleur|mal de|fi[èe]vre|toux|allergie|infection|rhume|grippe|migraine|nausée|brûlure|gastrite|insomnie|anxiété|tension|cholest[ée]rol|diab[èe]te)\b/i.test(t)) {
    return "symptom";
  }
  return "free";
}

const KIND_HINT_CLS: Record<DetectedKind, string> = {
  barcode: "bg-amber-50 text-amber-700 ring-amber-200",
  atc: "bg-violet-50 text-violet-700 ring-violet-200",
  symptom: "bg-sky-50 text-sky-700 ring-sky-200",
  free: "bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-700)] ring-[var(--sahha-primary-200)]",
};

const KIND_HINT_ICON: Record<DetectedKind, React.ReactNode> = {
  barcode: <Barcode size={11} weight="bold" />,
  atc: <Tag size={11} weight="bold" />,
  symptom: <Question size={11} weight="bold" />,
  free: <Sparkle size={11} weight="bold" />,
};

const KIND_ICON: Record<Suggestion["kind"], { icon: React.ReactNode; cls: string }> = {
  medicament:  { icon: <Pill weight="duotone" size={16} />,      cls: "text-[var(--sahha-primary-600)]" },
  dci:         { icon: <Flask weight="duotone" size={16} />,     cls: "text-violet-600" },
  laboratoire: { icon: <Buildings weight="duotone" size={16} />, cls: "text-sky-600" },
  classe:      { icon: <Tag weight="duotone" size={16} />,       cls: "text-amber-600" },
};

export function MedicamentSearch({ defaultValue = "" }: Props) {
  const t = useTranslations("medicament");
  const locale = useLocale();
  const KIND_LABEL: Record<Suggestion["kind"], string> = {
    medicament: t("search.categoryLabels.medicament"),
    dci: t("search.categoryLabels.dci"),
    laboratoire: t("search.categoryLabels.laboratoire"),
    classe: t("search.categoryLabels.classe"),
  };
  const KIND_HINT_LABEL: Record<DetectedKind, string> = {
    barcode: t("search.hints.barcode"),
    atc: t("search.hints.atc"),
    symptom: t("search.hints.symptom"),
    free: t("search.hints.free"),
  };
  const placeholderRotating = (t.raw("search.placeholder.rotating") as unknown as string[]) ?? [t("search.placeholder.default")];
  const router = useRouter();
  const params = useSearchParams();
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Sync sur back/forward navigation
  useEffect(() => {
    const next = params.get("q") ?? "";
    setValue(next);
  }, [params]);

  // Placeholder rotatif
  useEffect(() => {
    if (value.length > 0) return;
    const tm = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % placeholderRotating.length);
    }, 2800);
    return () => clearInterval(tm);
  }, [value, placeholderRotating.length]);

  // Outside click → ferme dropdown
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced fetch live
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/medicaments/search?q=${encodeURIComponent(q)}`, {
          signal: ctrl.signal,
        });
        if (!res.ok) {
          setSuggestions([]);
          return;
        }
        const data = (await res.json()) as { results: Suggestion[] };
        setSuggestions(data.results || []);
        setActiveIdx(-1);
      } catch (err) {
        if ((err as Error).name !== "AbortError") setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value]);

  const kind = useMemo(() => detectKind(value), [value]);
  const showHint = value.trim().length >= 2 && kind !== "free";

  // Tracking recherche médicaments — jusqu'ici jamais compté dans l'admin
  // (/admin/analytics/recherches ne voit que la recherche médecins via
  // SearchBar.tsx). Même pattern fire-and-forget que SearchBar : track()
  // (Plausible/GA4/PostHog) + /api/track/search (DB SearchQuery, source
  // dédiée "medicaments-*" pour ne pas polluer les stats spé/ville medecins).
  function trackMedicamentSearch(q: string, source: "form_submit" | "suggestion_click") {
    const qTrim = q.trim();
    if (qTrim.length < 2) return;
    track("search:performed", { q: qTrim, locale, source: `medicaments-${source}` });
    try {
      fetch("/api/track/search", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ q: qTrim, locale, source: `medicaments-${source}` }),
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => {});
    } catch {
      // analytics ne doit jamais empêcher la navigation
    }
  }

  function pushSearch(q: string) {
    const sp = new URLSearchParams();
    if (q.trim()) sp.set("q", q.trim());
    trackMedicamentSearch(q, "form_submit");
    startTransition(() => {
      router.push(`/medicaments${sp.toString() ? `?${sp.toString()}` : ""}`);
    });
  }

  function navigate(href: string) {
    setOpen(false);
    trackMedicamentSearch(value, "suggestion_click");
    startTransition(() => {
      router.push(href);
    });
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setOpen(false);
    if (activeIdx >= 0 && suggestions[activeIdx]) {
      navigate(suggestions[activeIdx].href);
    } else {
      pushSearch(value);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  function clear() {
    setValue("");
    setSuggestions([]);
    pushSearch("");
    inputRef.current?.focus();
  }

  // Group suggestions par kind pour affichage segmenté
  const grouped = useMemo(() => {
    const order: Suggestion["kind"][] = ["medicament", "dci", "classe", "laboratoire"];
    const groups: Record<Suggestion["kind"], Suggestion[]> = {
      medicament: [],
      dci: [],
      classe: [],
      laboratoire: [],
    };
    for (const s of suggestions) groups[s.kind].push(s);
    return order
      .filter((k) => groups[k].length > 0)
      .map((k) => ({ kind: k, items: groups[k] }));
  }, [suggestions]);

  return (
    <div ref={containerRef} className="relative">
      <form onSubmit={onSubmit} autoComplete="off">
        <div className="relative">
          <MagnifyingGlass
            weight="bold"
            size={18}
            className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-[var(--sahha-text-500)]"
          />
          <input
            ref={inputRef}
            type="text"
            name="medicament-search"
            placeholder={placeholderRotating[placeholderIdx] ?? placeholderRotating[0]}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            inputMode={kind === "barcode" ? "numeric" : "search"}
            autoComplete="off"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            className="h-12 w-full rounded-full bg-white pl-11 pr-12 text-[14px] font-semibold text-[var(--sahha-text-900)] shadow-sm ring-1 ring-[var(--sahha-border-subtle)] placeholder:text-[var(--sahha-text-400)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-500)]"
          />
          {loading && (
            <Spinner
              size={16}
              weight="bold"
              className="absolute end-9 top-1/2 -translate-y-1/2 animate-spin text-[var(--sahha-text-400)]"
            />
          )}
          {value && (
            <button
              type="button"
              onClick={clear}
              aria-label={t("search.clear")}
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[var(--sahha-text-500)] transition hover:bg-[var(--sahha-surface-1)]"
            >
              <X weight="bold" size={14} />
            </button>
          )}
        </div>

        {showHint && (
          <div className="absolute start-4 -bottom-7 z-10">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ${KIND_HINT_CLS[kind]}`}
            >
              {KIND_HINT_ICON[kind]}
              {KIND_HINT_LABEL[kind]}
            </span>
          </div>
        )}
      </form>

      {/* Dropdown live multi-axes */}
      {open && value.trim().length >= 2 && (
        <div className="absolute start-0 end-0 z-30 mt-2 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-[var(--sahha-border-subtle)]">
          {suggestions.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center">
              <p className="text-[12.5px] font-bold text-[var(--sahha-text-700)]">{t("search.noSuggestion")}</p>
              <p className="mt-1 text-[11px] text-[var(--sahha-text-500)]">
                {t("search.pressEnter")}
              </p>
            </div>
          ) : (
            <ul className="max-h-[26rem] overflow-y-auto py-1">
              {grouped.map((group) => (
                <li key={group.kind}>
                  <p className="sticky top-0 bg-white px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--sahha-text-400)]">
                    {KIND_LABEL[group.kind]}
                  </p>
                  <ul>
                    {group.items.map((s) => {
                      const flatIdx = suggestions.indexOf(s);
                      const isActive = flatIdx === activeIdx;
                      const ki = KIND_ICON[s.kind];
                      return (
                        <li key={`${s.kind}-${s.label}`}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setActiveIdx(flatIdx)}
                            onClick={() => navigate(s.href)}
                            className={`flex w-full items-center gap-3 px-3 py-2 text-left transition ${
                              isActive
                                ? "bg-[var(--sahha-primary-50)]"
                                : "hover:bg-[var(--sahha-surface-1)]"
                            }`}
                          >
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-surface-1)] ${ki.cls}`}>
                              {ki.icon}
                            </span>
                            <span className="min-w-0 flex-1">
                              <p className="truncate text-[13px] font-bold text-[var(--sahha-text-900)]">
                                {highlight(s.label, value)}
                              </p>
                              {s.hint && (
                                <p className="truncate text-[11px] text-[var(--sahha-text-500)]">
                                  {s.hint}
                                </p>
                              )}
                            </span>
                            {s.kind === "medicament" && (
                              <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--sahha-text-400)]">
                                {t("search.fiche")}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}

              {/* Footer : aller à la recherche complète */}
              <li className="border-t border-[var(--sahha-border-subtle)]">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pushSearch(value)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12px] font-bold text-[var(--sahha-primary-700)] transition hover:bg-[var(--sahha-surface-1)]"
                >
                  <MagnifyingGlass size={13} weight="bold" />
                  {t("search.viewAllFor")}
                  <span className="font-extrabold">«&nbsp;{value.trim()}&nbsp;»</span>
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Surligne la partie de `text` qui matche `query` (insensible à la casse). */
function highlight(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-[var(--sahha-primary-100)] px-0.5 text-[var(--sahha-primary-700)]">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

const SYMPTOM_CHIPS: { key: string; query: string; icon: React.ReactNode }[] = [
  { key: "pain",         query: "antalgique antipyretique", icon: <Pill size={12} weight="fill" /> },
  { key: "headache",     query: "céphalée",                 icon: <Question size={12} weight="fill" /> },
  { key: "cough",        query: "antitussif",               icon: <Pill size={12} weight="fill" /> },
  { key: "allergy",      query: "antihistaminique",         icon: <Pill size={12} weight="fill" /> },
  { key: "antibiotic",   query: "antibiotique",             icon: <Flask size={12} weight="fill" /> },
  { key: "hypertension", query: "antihypertenseur",         icon: <Pill size={12} weight="fill" /> },
  { key: "diabetes",     query: "antidiabétique",           icon: <Pill size={12} weight="fill" /> },
  { key: "heartburn",    query: "antiacide",                icon: <Pill size={12} weight="fill" /> },
  { key: "anxiety",      query: "anxiolytique",             icon: <Pill size={12} weight="fill" /> },
];

export function SymptomChips() {
  const t = useTranslations("medicament");
  return (
    <div className="flex flex-wrap gap-1.5">
      {SYMPTOM_CHIPS.map((s) => (
        <a
          key={s.key}
          href={`/medicaments?q=${encodeURIComponent(s.query)}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[11.5px] font-bold text-white backdrop-blur transition hover:bg-white/25"
        >
          <span className="text-white/85">{s.icon}</span>
          {t(`symptomChips.${s.key}` as never)}
        </a>
      ))}
    </div>
  );
}
